import * as Page from '../../components/content/page-header';
import { Content } from '../../components/layout/content';
import * as Button from '../../components/button/button';
import { Icons } from '../../components/icons/icons';
import { PaginatedTable } from '../../components/table/paginated-table';
import usePageState from '../../hooks/use-page-state';
import { Modes } from '../../types/page-modes';
import { Actions, Dialog, Title, Content as DialogContent } from '../../components/dialog/dialog';
import { Select2 } from '../../components/forms/select';
import { Label, TextInputLarge } from '../../components/forms/input';
import { FieldValues, useForm } from 'react-hook-form';
import { ExponentiationFormatter, LinkFormatter, NumberFormatter, ReplacementFormatter, RoundingFormatter, TimeFormatter, ValueFormatter } from './types/formatters';
import ReactJson from 'react-json-view';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router';
import React, { useState } from 'react';
import { Table } from '../../components/table/table';
import { ActionColumn, CellRenderer, GearIconColumn } from '../../components/table/columns';
import { Pageable, Query, usePagination } from '../../hooks/use-pagination';
import { useDeleteMutation, usePostMutation, usePutMutation } from '../../hooks/use-mutations';
import { RevertToMaster2 } from '../../components/revert-to-master/revert-to-master-2';
import { NotInSync } from '../../components/not-in-sync/not-in-sync';
import tenantService from '../../services/tenant-service';
import { Checkbox2 } from '../../components/forms/checkbox';
import { RecordStatus } from '../../types/record-status';

export const ValueFormatters = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit, reset, watch, setValue, getValues, formState: { errors } } = useForm();
  const type = watch('type') as string;
  const params = useParams();
  const formatterId = params.id as string;
  const [query, setQuery] = useState<Query>({});
  const formatter = usePagination<Pageable<any>>(`/formatter${formatterId ? `/${formatterId}/composition/details` : ''}`, query);
  const postMutation = usePostMutation({ path: '/formatter', cacheKey: ['/formatter', `/formatter/${formatterId}/composition/details`] });
  const putMutation = usePutMutation({ path: `/formatter/${pageState.item?.id as string}`, cacheKey: `/formatter/${formatterId}/composition/details` });
  const deleteFormatter = useDeleteMutation({ cacheKey: [`/formatter`, `/formatter/${formatterId}/composition/details`] });

  const onSubmit = (data: FieldValues): void => {
    if (data.value) {
      data.value = parseFloat(data.value as string);
    }
    if (params.id) {
      data.compositionParentId = params.id;
    }
    !data.id ? postMutation.mutate(data) : putMutation.mutate(data);
    setPageState(Modes.BROWSE);
  };

  const addFormatter = (): void => {
    reset();
    setPageState(Modes.CREATE);
  };

  const onDeleteConfirm = (): void => {
    deleteFormatter.mutate({ path: `/formatter/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const onUpdate = (item: ValueFormatter): void => {
    reset();

    for (const key in item) {
      if (key === 'formatter') {
        switch(true) {
          case [ADDITION, DIVISION, MULTIPLICATION, SUBTRACTION].includes(item.type as string): {
            const formatter = item[key as keyof ValueFormatter] as unknown as NumberFormatter;
            setValue('value', formatter.value);
            break;
          }
          case item.type === LINK: {
            const formatter = item[key as keyof ValueFormatter] as unknown as LinkFormatter;
            setValue('textExpression', formatter.text?.expression);
            setValue('textReplacement', formatter.text?.replacement);
            setValue('urlExpression', formatter.url?.expression);
            setValue('urlReplacement', formatter.url?.replacement);
            break;
          }
          case item.type === EXPONENTIATION: {
            const formatter = item[key as keyof ValueFormatter] as unknown as ExponentiationFormatter;
            setValue('power', formatter.power);
            break;
          }
          case item.type === REPLACEMENT: {
            const formatter = item[key as keyof ValueFormatter] as unknown as ReplacementFormatter;
            setValue('expression', formatter.text?.expression);
            setValue('replacement', formatter.text?.replacement);
            break;
          }
          case item.type === ROUNDING: {
            const formatter = item[key as keyof ValueFormatter] as unknown as RoundingFormatter;
            setValue('precision', formatter.precision);
            break;
          }
          case item.type === TIME: {
            const formatter = item[key as keyof ValueFormatter] as unknown as TimeFormatter;
            setValue('format', formatter.format);
            break;
          }
        }
      } else {
        setValue(key, item[key as keyof ValueFormatter]);
      }
    }
    setValue('locked', item['recordStatus' as keyof ValueFormatter] === RecordStatus.GM_LOCKED);
    setPageState(Modes.UPDATE, item);
  };

  const columns = [
    ...(!params.id ? GearIconColumn() : []),
    { accessor: 'formatterId', Header: 'Formatter Id', sort: true, width: 300, Cell: CellRenderer },
    { accessor: 'type', Header: 'Type', sort: true, width: 268, Cell: CellRenderer },
    { accessor: 'formatter', Header: 'Formatter', width: 350, Cell: (item: any): JSX.Element => {
      return <div>
        <ReactJson
          src={item.row.original.formatter}
          collapsed
          name={false}
          displayDataTypes={false}
          theme="grayscale:inverted"
          displayObjectSize={false}
          enableClipboard={false}
          iconStyle="square"
        />
        {item.row.original.type === COMPOSITION && <Link to={`/settings/value-formatters/${item.row.original.id as string}`}>Details</Link>}
      </div>;
    } },
    ActionColumn({ onDelete: (item: ValueFormatter): void => setPageState(Modes.DELETE, item), onUpdate })
  ];

  return <>
    <Content>
      <Page.Header>
        <Page.Title>
          {formatterId ? (
            <Link to="/settings/value-formatters" className="page-title-link">
              <Icons.LeftArrow className="page-header-back" />
              Value Formatters
            </Link>
          ) : 'Value Formatters'}
          <NotInSync path="formatter" />
        </Page.Title>
        <Page.SubTitle>
          Value Formatters provide the ability to customize the dispaly of value within DisasterAWARE.
          <div>
            <RevertToMaster2 path="formatter" />
            <Button.LightBlackSmall onClick={addFormatter}>
              <Icons.Add />
              Add Formatter
            </Button.LightBlackSmall>
          </div>
        </Page.SubTitle>
      </Page.Header>
      {!params.id ? (
        <PaginatedTable isLoading={formatter.isLoading} columns={columns} data={formatter.data || []} onChange={setQuery} />
      ) : (
        <Table isLoading={formatter.isLoading} columns={columns} rows={(Array.isArray(formatter.data) ? formatter.data as any : [])} />
      )}
    </Content>
    {(pageState.mode === Modes.CREATE || pageState.mode === Modes.UPDATE) && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Title><span>{pageState.mode === Modes.CREATE ? 'Add' : 'Edit'} Value Formatter</span></Title>
          <DialogContent>
            <>
              {(!params.id && type) && (
                <div className="mb-4">
                  <Label>Formatter Id</Label>
                  <TextInputLarge { ...register('formatterId', { required: 'You must provide a value for Formatter Id.' })} errors={errors} />
                </div>
              )}
              <div className="mb-4">
                <Label>Formatter Type</Label>
                <Select2 { ...register('type', { required: 'You must provide a value for Formatter Type.' })} errors={errors}>
                  {formatterTypes.filter(unit => !params.id || (params.id && unit.value !== COMPOSITION)).map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
                </Select2>
              </div>
              {[ADDITION, DIVISION, MULTIPLICATION, SUBTRACTION].includes(type) && (
                <div className="mb-4">
                  <Label>Value</Label>
                  <TextInputLarge { ...register('value')} />
                </div>
              )}
              {type === EXPONENTIATION && (
                <div className="mb-4">
                  <Label>Power</Label>
                  <TextInputLarge { ...register('power')} />
                </div>
              )}
              {type === LINK && (
                <>
                  <div className="mb-4">
                    <Label>Text Expression</Label>
                    <TextInputLarge { ...register('textExpression')} />
                  </div>
                  <div className="mb-4">
                    <Label>Text Replacement</Label>
                    <TextInputLarge { ...register('textReplacement')} />
                  </div>
                  <div className="mb-4">
                    <Label>Url Expression</Label>
                    <TextInputLarge { ...register('urlExpression')} />
                  </div>
                  <div className="mb-4">
                    <Label>Url Replacement</Label>
                    <TextInputLarge { ...register('urlReplacement')} />
                  </div>
                </>
              )}
              {type === REPLACEMENT && (
                <>
                  <div className="mb-4">
                    <Label>Expression</Label>
                    <TextInputLarge { ...register('expression')} />
                  </div>
                  <div className="mb-4">
                    <Label>Replacement</Label>
                    <TextInputLarge { ...register('replacement')} />
                  </div>
                </>
              )}
              {type === ROUNDING && (
                <>
                  <div className="mb-4">
                    <Label>Precision</Label>
                    <TextInputLarge { ...register('precision')} />
                  </div>
                </>
              )}
              {type === TIME && (
                <>
                  <div className="mb-4">
                    <Label>Format</Label>
                    <TextInputLarge { ...register('format')} />
                  </div>
                </>
              )}
              {tenantService.isGoldMaster() ? (
                <div className="mb-4">
                  <Checkbox2 {...register('locked')} defaultChecked={getValues().locked} />
                  </div>
                ): <></>}
            </>
          </DialogContent>
          <Actions>
            <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
            <Button.Primary type="submit">
              {pageState.mode === Modes.CREATE ? <><Icons.Add /> Add</> : 'Update'}
            </Button.Primary>
          </Actions>
        </form>
      </Dialog>
    )}
    {pageState.mode === Modes.DELETE && (
      <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
        <Title>Delete Value Formatter</Title>
        <DialogContent>Are you sure you want to delete?</DialogContent>
        <Actions>
          <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
          <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
        </Actions>
      </Dialog>
    )}
  </>;
};

const ADDITION = 'ADDITION';
const COMPOSITION = 'COMPOSITION';
const DIVISION = 'DIVISION';
const EXPONENTIATION = 'EXPONENTIATION';
const LINK = 'LINK';
const MULTIPLICATION = 'MULTIPLICATION';
const REPLACEMENT = 'REPLACEMENT';
const ROUNDING = 'ROUNDING';
const SUBTRACTION = 'SUBTRACTION';
const TIME = 'TIME';

const formatterTypes = [
  { value: '', label: 'Select formatter type' },
  { value: ADDITION, label: 'Addition' },
  { value: COMPOSITION, label: 'Composition' },
  { value: DIVISION, label: 'Division' },
  { value: EXPONENTIATION, label: 'Exponentiation' },
  { value: LINK, label: 'Link' },
  { value: MULTIPLICATION, label: 'Multiplication' },
  { value: REPLACEMENT, label: 'Replacement' },
  { value: ROUNDING, label: 'Rounding' },
  { value: SUBTRACTION, label: 'Subtraction' },
  { value: TIME, label: 'Time' },
];
